import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SocketContext } from '../../context/socket';
import { SOCKET_URL } from '../../socketConfig';
import PresenceStatsBar from './PresenceStatsBar';
import PlayerPresenceList from './PlayerPresenceList';

const STATUS_ORDER = { online: 0, idle: 1, offline: 2 };
const isDebug =
  process.env.NODE_ENV === 'development' ||
  String(process.env.REACT_APP_PRESENCE_DEBUG || '').toLowerCase() === 'true';

const HEX_ID_RE = /^[a-f0-9]{24}$/i;

const candidateKeys = (s = {}) => [
  s.handle,
  s.player?.playerId,
  s.player?.handle,
  s.player?.name,
  s.displayName,
  s.playerId,
  s._id,
  s.mongoId,
];

const chooseKey = (s = {}) => {
  const keys = candidateKeys(s).filter(Boolean);
  if (!keys.length) return null;
  // Prefer first non-hex if available
  const nonHex = keys.find((k) => !HEX_ID_RE.test(String(k)));
  return nonHex || keys[0];
};

function pickDisplay(session = {}) {
  return (
    session.player?.name ||
    session.player?.playerId ||
    session.player?.handle ||
    session.player?.displayName ||
    session.handle ||
    session.displayName ||
    session.playerId ||
    session._id ||
    session.mongoId ||
    null
  );
}

function pickPhoto(session = {}) {
  return (
    session.player?.profilePhoto ||
    session.profilePhoto ||
    session.avatar ||
    session.photo ||
    null
  );
}

function aggregatePlayer(sessions = []) {
  if (!sessions.length) {
    return {
      status: 'offline',
      latencyMs: null,
      devices: [],
      lastUpdatedAt: null,
      lastSeenAt: null,
    };
  }

  let hasOnline = false;
  let hasIdle = false;
  let lastUpdatedAt = 0;
  let lastSeenAt = 0;
  let latencyTotal = 0;
  let latencyCount = 0;
  const devices = new Set();

  sessions.forEach((s) => {
    if (!s) return;
    const status =
      s.status || s.presenceStatus || (s.online ? 'online' : 'offline');
    if (status === 'online') hasOnline = true;
    else if (status === 'idle' || status === 'away') hasIdle = true;

    const lu = s.updatedAt || s.lastSeen || s.timestamp;
    if (lu && lu > lastUpdatedAt) lastUpdatedAt = lu;
    const ls = s.lastSeenAt || s.lastSeen || s.timestamp;
    if (ls && ls > lastSeenAt) lastSeenAt = ls;

    if (typeof s.latencyMs === 'number') {
      latencyTotal += s.latencyMs;
      latencyCount += 1;
    } else if (typeof s.latency === 'number') {
      latencyTotal += s.latency;
      latencyCount += 1;
    } else if (typeof s.network?.latencyMs === 'number') {
      latencyTotal += s.network.latencyMs;
      latencyCount += 1;
    } else if (typeof s.network?.latency === 'number') {
      latencyTotal += s.network.latency;
      latencyCount += 1;
    }

    if (Array.isArray(s.devices)) {
      s.devices.forEach((d) => {
        if (d?.deviceId) devices.add(String(d.deviceId).toLowerCase());
      });
    }

    const dev = s.device || s.deviceType;
    if (dev) devices.add(String(dev).toLowerCase());
  });

  const status = hasOnline ? 'online' : hasIdle ? 'idle' : 'offline';
  const latencyMs = latencyCount
    ? Math.round(latencyTotal / latencyCount)
    : null;

  return {
    status,
    latencyMs,
    devices: Array.from(devices),
    lastUpdatedAt: lastUpdatedAt || null,
    lastSeenAt: lastSeenAt || null,
  };
}

export default function PlayerPresenceDirectory() {
  const { socket } = useContext(SocketContext) || {};
  const [players, setPlayers] = useState({});
  const [stats, setStats] = useState({
    online: 0,
    idle: 0,
    offline: 0,
    recent: 0,
  });
  const sessionStoreRef = useRef(new Map()); // sessionId -> session

  const rebuild = (opts = {}) => {
    const allowHexWithoutFriendly = !!opts.allowHexWithoutFriendly;
    const byPlayer = {};
    sessionStoreRef.current.forEach((session) => {
      let pid = chooseKey(session);
      const hasFriendly =
        session.handle ||
        session.player?.handle ||
        session.player?.playerId ||
        session.player?.name;
      if (!pid) return;
      if (!allowHexWithoutFriendly && HEX_ID_RE.test(String(pid)) && !hasFriendly) {
        return; // skip pure mongoId entries with no friendly handle
      }
      const displayName = pickDisplay(session);
      const profilePhoto = pickPhoto(session);
      byPlayer[pid] = byPlayer[pid] || {
        playerId: pid,
        sessions: [],
        displayName: null,
        profilePhoto: null,
      };
      if (displayName && !byPlayer[pid].displayName) {
        byPlayer[pid].displayName = displayName;
      }
      if (profilePhoto && !byPlayer[pid].profilePhoto) {
        byPlayer[pid].profilePhoto = profilePhoto;
      }
      byPlayer[pid].sessions.push(session);
    });

    const nextPlayers = {};
    Object.keys(byPlayer).forEach((pid) => {
      const agg = aggregatePlayer(byPlayer[pid].sessions);
      nextPlayers[pid] = {
        playerId: pid,
        displayName:
          byPlayer[pid].displayName ||
          byPlayer[pid].sessions[0]?.displayName ||
          byPlayer[pid].sessions[0]?.player?.name ||
          byPlayer[pid].sessions[0]?.player?.handle ||
          byPlayer[pid].sessions[0]?.handle ||
          byPlayer[pid].sessions[0]?.player?.playerId ||
          byPlayer[pid].sessions[0]?.playerId ||
          pid,
        profilePhoto:
          byPlayer[pid].profilePhoto ||
          byPlayer[pid].sessions[0]?.player?.photo ||
          byPlayer[pid].sessions[0]?.player?.profilePhoto ||
          byPlayer[pid].sessions[0]?.profilePhoto ||
          null,
        status: agg.status,
        latencyMs: agg.latencyMs,
        devices: agg.devices,
        lastUpdatedAt: agg.lastSeenAt || agg.lastUpdatedAt,
      };
    });
    setPlayers(nextPlayers);

    const now = Date.now();
    const recentCutoff = now - 5 * 60 * 1000;
    let online = 0;
    let idle = 0;
    let offline = 0;
    let recent = 0;
    Object.values(nextPlayers).forEach((p) => {
      if (p.status === 'online') online += 1;
      else if (p.status === 'idle') idle += 1;
      else offline += 1;
      if (p.lastUpdatedAt && p.lastUpdatedAt >= recentCutoff) recent += 1;
    });
    setStats({ online, idle, offline, recent });
  };

  const handleSnapshot = (payload) => {
    const sessions = payload?.sessions || payload || [];
    const list = Array.isArray(sessions)
      ? sessions
      : Object.entries(sessions).map(([sid, val]) => ({
          ...(val || {}),
          sessionId: sid,
        }));
    const store = new Map();
    list.forEach((s) => {
      const sid = s.sessionId || s.session_id || s.id;
      if (!sid) return;
      store.set(sid, s);
    });
    sessionStoreRef.current = store;
    rebuild();
  };

  const applyDiff = (diff) => {
    const store = sessionStoreRef.current;

    const normalizeList = (input) => {
      const toSessionId = (val, sid) =>
        val?.sessionId ||
        val?.session_id ||
        val?.id ||
        val?.mongoId ||
        val?.playerId ||
        val?.handle ||
        sid ||
        null;

      if (Array.isArray(input)) {
        return input.map((val) => ({
          ...(val || {}),
          sessionId: toSessionId(val, null),
        }));
      }

      return Object.entries(input || {}).map(([sid, val]) => ({
        ...(val || {}),
        sessionId: toSessionId(val, sid),
      }));
    };

    const add = normalizeList(diff.add || {});
    const update = normalizeList(diff.update || {});
    const remove = diff.remove || [];

    add.forEach((s) => {
      const sid =
        s.sessionId ||
        s.session_id ||
        s.id ||
        s.mongoId ||
        s.playerId ||
        s.handle;
      if (!sid) return;
      // prevent duplicates keyed by different ids: collapse to player key when possible
      store.set(sid, { ...store.get(sid), ...s });
    });

    update.forEach((s) => {
      const sid =
        s.sessionId ||
        s.session_id ||
        s.id ||
        s.mongoId ||
        s.playerId ||
        s.handle;
      if (!sid) return;
      const prev = store.get(sid) || {};
      store.set(sid, { ...prev, ...s });
    });

    remove.forEach((sid) => {
      const id =
        typeof sid === 'object'
          ? sid?.sessionId || sid?.session_id || sid?.id
          : sid;
      if (id) store.delete(id);
    });

    sessionStoreRef.current = store;
    rebuild();
  };

  useEffect(() => {
    if (!socket) return undefined;

    const onSnapshot = (payload) => {
      handleSnapshot(payload);
    };
    const onDiff = (payload) => {
      applyDiff(payload);
    };
    const onUpdate = (payload) => {
      applyDiff({ update: [payload] });
    };
    const onRemove = (payload) => {
      if (Array.isArray(payload)) applyDiff({ remove: payload });
      else if (payload?.sessionId || payload?.session_id || payload?.id) {
        applyDiff({ remove: [payload] });
      }
    };

    socket.on('presence:snapshot', onSnapshot);
    socket.on('presence:diff', onDiff);
    socket.on('presence:update', onUpdate);
    socket.on('presence:remove', onRemove);
    socket.on('presence:remove:detail', onRemove);
    socket.emit('presence:snapshot:request');

    return () => {
      socket.off('presence:snapshot', onSnapshot);
      socket.off('presence:diff', onDiff);
      socket.off('presence:update', onUpdate);
      socket.off('presence:remove', onRemove);
      socket.off('presence:remove:detail', onRemove);
    };
  }, [socket]);

  const handleClick = (playerId) => {
    // Hook for future integrations (chat / call / orbit preview).
    // eslint-disable-next-line no-console
    console.log('[presence-directory] player clicked', playerId);
  };

  const memoPlayers = useMemo(() => players, [players]);

  return (
    <div className="container py-3">
      <PresenceStatsBar stats={stats} />
      <PlayerPresenceList players={memoPlayers} onPlayerClick={handleClick} />
    </div>
  );
}

// Sorting helper exported for tests if needed
export function comparePlayers(a, b) {
  const sa = STATUS_ORDER[a.status] ?? 3;
  const sb = STATUS_ORDER[b.status] ?? 3;
  if (sa !== sb) return sa - sb;
  return String(a.playerId || '').localeCompare(String(b.playerId || ''));
}
